import { useTranslation } from "@sagot/i18n";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Button, ErrorState, Icon } from "@sagot/ui";
import type { ReactNode } from "react";
import { META_PERMISSIONS } from "../channels/messenger-dialogs";

const P = "deployKnowledge.metaCallback";

interface RestartProps {
  onRestart: () => void;
  busy: boolean;
}

/** "Facebook shared n Pages with Sagot." — what the person ticked; says how many can be used when fewer. */
export function SharedPagesNote({ count, available }: { count: number; available: number }) {
  const { t } = useTranslation();
  const text = available < count ? t(`${P}.sharedPagesPartial`, { count, available }) : t(`${P}.sharedPages`, { count });
  return <p className="text-xs text-fg-subtle">{text}</p>;
}

/** Shared Pages Facebook gave no token for: named by ID (Facebook gives no name) with the fix. */
export function UnavailablePages({ ids }: { ids: readonly string[] }) {
  const { t } = useTranslation();
  if (ids.length === 0) return null;
  return (
    <section role="status" aria-labelledby="unavailable-pages-title" className="rounded-md border border-line bg-surface-2 p-3.5 text-xs" data-testid="unavailable-pages">
      <h3 id="unavailable-pages-title" className="flex items-center gap-1.5 text-sm font-bold">
        <Icon name="alert-triangle" className="size-4 shrink-0 text-warning" />
        {t(`${P}.unavailableTitle`, { count: ids.length })}
      </h3>
      <ul className="mt-2 space-y-1">
        {ids.map((id) => <li key={id} className="font-mono text-fg">{t(`${P}.unavailableId`, { id })}</li>)}
      </ul>
      <p className="mt-2 text-fg-muted">{t(`${P}.unavailableBody`)}</p>
    </section>
  );
}

/** "Don't see your Page?" disclosure with the "Choose Pages again" restart. */
export function RepickHelp({ onRestart, busy }: RestartProps) {
  const { t } = useTranslation();
  return (
    <Accordion type="single" collapsible className="rounded-md border border-line">
      <AccordionItem value="repick" className="border-b-0">
        <AccordionTrigger>
          <span className="flex items-center gap-2">
            <Icon name="help-circle" className="text-fg-subtle" />
            {t(`${P}.repickTitle`)}
          </span>
        </AccordionTrigger>
        <AccordionContent className="space-y-2">
          <p>{t(`${P}.repickBody`)}</p>
          <p>{t(`${P}.repickBusiness`)}</p>
          <Button variant="secondary" size="sm" className="mt-1" onClick={onRestart} loading={busy}>
            <Icon name="refresh-cw" />
            {t(`${P}.repickAction`)}
          </Button>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

interface MissingPermissionsProps extends RestartProps {
  scopes: readonly string[];
  back: ReactNode;
}

/** The person declined required permissions in Facebook's popup: say which, offer a way back in. */
export function MissingPermissions({ scopes, onRestart, busy, back }: MissingPermissionsProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <ErrorState title={t(`${P}.missingPermsTitle`)} description={t(`${P}.missingPermsBody`)} />
      <ul aria-label={t(`${P}.missingPermsList`)} className="space-y-2.5 text-sm">
        {scopes.map((scope) => {
          const known = META_PERMISSIONS.find((permission) => permission.scope === scope);
          return (
            <li key={scope} className="flex items-start gap-2.5">
              <Icon name="x-circle" className="mt-0.5 size-4 text-danger" />
              <span>
                <span className="font-mono text-xs">{scope}</span>
                <span className="ml-2 text-xs font-semibold text-danger">{t(`${P}.notGranted`)}</span>
                {known ? <span className="block text-xs text-fg-subtle">{t(`deployKnowledge.channels.messenger.${known.key}`)}</span> : null}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap justify-center gap-2">
        {back}
        <Button onClick={onRestart} loading={busy}>
          <Icon name="log-in" />
          {t(`${P}.missingPermsAction`)}
        </Button>
      </div>
    </div>
  );
}
